import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo } from '../components/Icons.jsx';
import { Intro, Done, mainStyle } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';
import StuckPopIn from '../components/StuckPopIn.jsx';

const easyQs = [
  {
    q: 'A school surveyed 200 students.',
    table: { headers: ['', 'Female', 'Male'], rows: [['Wears glasses', 60, 40], ['No glasses', 50, 50]] },
    question: 'P(student picked at random wears glasses)?',
    options: ['1/4', '1/2', '3/4', '2/5'],
    answer: 1,
    working: '(60 + 40) / 200 = 100/200 = 1/2.'
  },
  {
    q: 'A clinic tracks 150 patients on two medications.',
    table: { headers: ['', 'On drug A', 'Not on A'], rows: [['On drug B', 30, 20], ['Not on B', 60, 40]] },
    question: 'P(patient is on drug A only)?',
    options: ['30/150', '60/150', '90/150', '50/150'],
    answer: 1,
    working: 'On A only = on A not on B = 60. 60/150 = 2/5.'
  },
  {
    q: 'A bag has 40 marbles: 15 red, 10 blue, 8 green, 7 yellow.',
    question: 'P(blue OR green)?',
    options: ['1/4', '9/20', '7/20', '3/8'],
    answer: 1,
    working: '(10 + 8) / 40 = 18/40 = 9/20.'
  },
  {
    q: 'A hospital records 240 patient outcomes.',
    table: { headers: ['', 'Recovered', 'Did not recover'], rows: [['Took drug X', 120, 40], ['Did not take X', 50, 30]] },
    question: 'P(patient recovered)?',
    options: ['7/12', '170/240', 'Both correct', '2/3'],
    answer: 2,
    working: '120 + 50 = 170 recovered. 170/240 = 17/24 ≈ 7.08/10. The two listed options 7/12 ≈ 140/240 and 170/240 — 7/12 is wrong. Correct fraction is 170/240 = 17/24.'
  }
];

const hardQs = [
  {
    q: 'A medical trial enrols 300 patients across two treatment arms.',
    table: { headers: ['', 'Improved', 'No change', 'Worsened'], rows: [['Drug', 90, 30, 30], ['Placebo', 40, 70, 40]] },
    question: 'P(patient improved | took the drug)?',
    options: ['90/300', '90/150', '90/130', '90/210'],
    answer: 1,
    working: 'Conditional: of 150 drug patients, 90 improved. 90/150 = 3/5.'
  },
  {
    q: 'A coin is biased: P(heads) = 0.6. Flipped 3 times.',
    question: 'P(exactly 2 heads)?',
    options: ['0.432', '0.288', '0.216', '0.576'],
    answer: 0,
    working: 'C(3,2) × 0.6² × 0.4 = 3 × 0.36 × 0.4 = 0.432.'
  },
  {
    q: 'A two-way table: 500 patients screened for 2 conditions.',
    table: { headers: ['', 'Condition A: yes', 'Condition A: no'], rows: [['Condition B: yes', 60, 90], ['Condition B: no', 140, 210]] },
    question: 'P(has condition A AND condition B)?',
    options: ['60/500', '60/200', '60/150', '150/500'],
    answer: 0,
    working: 'Joint probability: 60 patients have both, out of 500 total. 60/500 = 3/25.'
  },
  {
    q: 'A lab test has sensitivity 90% and specificity 80%. Disease prevalence is 5% in screened population.',
    question: 'Of all positive tests, P(actually have disease)? (PPV)',
    options: ['~5%', '~19%', '~50%', '~90%'],
    answer: 1,
    working: 'Of 1000: 50 disease (45 test+) + 950 no disease (190 false+). PPV = 45/(45+190) ≈ 19%.'
  },
  {
    q: 'A patient is offered a 30% chance of survival on Drug X for 5 years.',
    question: 'P(survives at least one of two independent 5-year periods)?',
    options: ['60%', '51%', '70%', '49%'],
    answer: 1,
    working: '1 − P(fails both) = 1 − 0.7² = 1 − 0.49 = 51%.'
  },
  {
    q: 'Tree diagram: a clinic receives patients. 40% need urgent care; of those, 25% are admitted. Of non-urgent, 5% are admitted.',
    question: 'P(admitted)?',
    options: ['10%', '13%', '30%', '15%'],
    answer: 1,
    working: '0.4 × 0.25 + 0.6 × 0.05 = 0.10 + 0.03 = 0.13 = 13%.'
  }
];

export default function DataProbability() {
  const [stage, setStage] = useState('intro');
  const [difficulty, setDifficulty] = useState('easy');
  const [ucatsen, setUcatsen] = useState(false);
  const [length, setLength] = useState(4);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);

  const pool = difficulty === 'easy' ? easyQs : hardQs;
  const questions = useMemo(() => {
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(length, pool.length));
  }, [pool, length, stage]);

  const q = questions[idx];
  const start = () => { setIdx(0); setAnswers([]); setPicked(null); setStage('drill'); };
  const next = () => {
    const correct = picked === q.answer;
    setAnswers([...answers, { ...q, given: picked, correct }]);
    setPicked(null);
    if (idx < questions.length - 1) setIdx(idx + 1);
    else setStage('done');
  };

  const correctCount = answers.filter((a) => a.correct).length;
  let wrongStreak = 0;
  for (let i = answers.length - 1; i >= 0; i--) {
    if (answers[i].correct) break;
    wrongStreak++;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={drillHeader}>
        <Link to="/practice" style={drillExit}><ArrowLeft width="16" height="16" />Exit</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight /><strong style={titleStyle}>Data probability</strong>
        </div>
        <div style={{ width: 60 }} />
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="QR" title="Data probability"
          body="Probability from two-way tables, frequency tables and tree diagrams. The most-failed shape in real QR."
          how={['Read the table or scenario', 'Compute the probability', 'Per-question rationale shown at the end']}
          difficulty={difficulty} setDifficulty={setDifficulty}
          difficultyOptions={[
            { v: 'easy', label: 'Foundation', sub: 'Direct table reading, simple ratios' },
            { v: 'hard', label: 'UCAT-style', sub: 'Conditional, joint, Bayes, trees' }
          ]}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
          extra={
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Length</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[4, 6, pool.length].map((n) => (
                  <button key={n} onClick={() => setLength(n)} style={lengthBtn(length === n)}>{n} questions</button>
                ))}
              </div>
            </div>
          }
        />
      )}

      {stage === 'drill' && <StuckPopIn wrongStreak={wrongStreak} slug="data-probability" sectionLabel="data & probability" />}
      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>Question {idx + 1} of {questions.length}</div>
          <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, marginBottom: 20 }}>
            <div style={{ width: `${((idx + 1) / questions.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', borderRadius: 99 }} />
          </div>

          <p style={{ margin: '0 0 14px', fontSize: 15, color: 'var(--text-2)' }}>{q.q}</p>

          {q.table && (
            <div style={{ marginBottom: 16, overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                <thead><tr>{q.table.headers.map((h, i) => (
                  <th key={i} style={{ padding: '8px 12px', background: 'var(--card-soft)', fontSize: 13, fontWeight: 700, textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}</tr></thead>
                <tbody>{q.table.rows.map((row, ri) => (
                  <tr key={ri}>{row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: 14, fontWeight: ci === 0 ? 700 : 500 }}>{cell}</td>
                  ))}</tr>
                ))}</tbody>
              </table>
            </div>
          )}

          <h3 style={{ margin: '12px 0 16px', fontSize: 17 }}>{q.question}</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {q.options.map((opt, i) => (
              <button key={i} onClick={() => setPicked(i)} style={mcqBtn(picked === i, i)}>{opt}</button>
            ))}
          </div>

          <button onClick={next} disabled={picked === null} className="btn btn-primary" style={{ opacity: picked === null ? 0.5 : 1 }}>
            {idx === questions.length - 1 ? 'Finish' : 'Next'} <ArrowRight width="14" height="14" />
          </button>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="QR" title="Data probability" stats={[
          { label: 'Correct', value: `${correctCount} / ${questions.length}` },
          { label: 'Accuracy', value: `${Math.round((correctCount / questions.length) * 100)}%` },
          { label: 'Mode', value: difficulty === 'easy' ? 'Foundation' : 'UCAT-style' }
        ]} onRestart={start}
          extra={<>
            <DrillResultBar slug="data-probability" score={correctCount} maxScore={questions.length} accuracyPct={Math.round((correctCount / questions.length) * 100)} metadata={{ difficulty, length, ucatsen }} />
            {(correctCount / questions.length) < 0.65 && (
              <TutorNudge tutor={tutorFor('qr')} reason="Two-way tables and Bayes-style questions are pattern-based — once you see them, you can't unsee them." kicker="Tables tripping you up" />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
              {answers.map((a, i) => (
                <div key={i} className="card" style={{ borderLeft: `4px solid ${a.correct ? 'var(--live-green)' : '#b91c1c'}` }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{a.question}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Your: <strong>{a.options[a.given]}</strong> · Correct: <strong>{a.options[a.answer]}</strong></div>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-2)', padding: '8px 12px', background: 'var(--card-soft)', borderRadius: 6 }}>{a.working}</p>
                </div>
              ))}
            </div>
          </>}
        />
      )}
    </div>
  );
}

const drillHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)' };
const drillExit = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 };
const titleStyle = { fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' };

function lengthBtn(active) {
  return { padding: '8px 16px', borderRadius: 99, cursor: 'pointer', fontSize: 14, fontWeight: 600, border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`, background: active ? 'var(--accent-soft)' : '#fff', color: active ? 'var(--brand-deep)' : 'var(--text-2)' };
}
function mcqBtn(active, i) {
  return { padding: '14px 18px', textAlign: 'left', fontSize: 15, fontWeight: 600, cursor: 'pointer', border: `2px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`, background: active ? 'var(--accent-soft)' : '#fff', color: 'var(--text)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 };
}
